import React, { useState, useMemo, useEffect, useCallback, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ProHeader from '../components/ProHeader/ProHeader';
import SEO from '../components/SEO/SEO';
import ProChart from '../components/ProChart/ProChart';
import DebugPanel from '../components/DebugPanel/DebugPanel';
import { useWebSocket } from '../hooks/useWebSocket';
import { useFilters } from '../hooks/useFilters';
import { useLivePrices } from '../hooks/useLivePrices';
import { useBtcPredictions } from '../hooks/useBtcPredictions';
import { useTradeSizeDistribution } from '../hooks/useTradeSizeDistribution';
import { useAllCoinNews } from '../hooks/useAllCoinNews';
import { usePolymarketWebSocket } from '../hooks/usePolymarketWebSocket';
import { useEtfFlows } from '../hooks/useEtfFlows';
import { useHyperliquidPositions } from '../hooks/useHyperliquidPositions';
import { useLanguage } from '../contexts/LanguageContext';
import { useInterfacePreference } from '../hooks/useInterfacePreference';
import { useSoundNotificationsContext } from '../contexts/SoundNotificationsContext';
import { useTopTrades } from '../hooks/useTopTrades';
import { useSignal } from '../hooks/useSignal';
import SignalWidget from '../components/SignalWidget/SignalWidget';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import HyperliquidPromo from '../components/PromoButtons/HyperliquidPromo';
import BitunixPromo from '../components/PromoButtons/BitunixPromo';
import { trackPageView } from '../utils/analytics';
import { API_BASE_URL } from '../utils/apiConfig';
import './TradingPagePro.css';

// Exchange 2-letter abbreviations
const EX_SHORT = {
  BINANCE: 'BN', BINANCE_FUTURES: 'BN',
  BYBIT: 'BY', OKX: 'OK', BITGET: 'BG', HYPERLIQUID: 'HL'
};
const getExShort = (ex) => EX_SHORT[ex?.toUpperCase()] || ex?.slice(0, 2).toUpperCase() || '??';

// Format dollar amount
function formatDollar(value) {
  if (!value && value !== 0) return '-';
  if (value >= 1e9) return '$' + (value / 1e9).toFixed(2) + 'B';
  if (value >= 1e6) return '$' + (value / 1e6).toFixed(2) + 'M';
  if (value >= 1e3) return '$' + (value / 1e3).toFixed(0) + 'K';
  return '$' + value.toFixed(0);
}

// Format time as HH:MM:SS
function formatTime(ts) {
  const d = new Date(ts);
  if (isNaN(d.getTime())) return '--:--:--';
  return d.getHours().toString().padStart(2, '0') + ':' +
         d.getMinutes().toString().padStart(2, '0') + ':' +
         d.getSeconds().toString().padStart(2, '0');
}

// Format time ago
function formatTimeAgo(ts) {
  const sec = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
  if (sec < 60) return sec + 's';
  if (sec < 3600) return Math.floor(sec / 60) + 'm';
  if (sec < 86400) return Math.floor(sec / 3600) + 'h';
  return Math.floor(sec / 86400) + 'd';
}

// Shrink font for long coin names
function coinSymStyle(name) {
  const len = name?.length || 0;
  if (len <= 4) return undefined;
  if (len <= 5) return { fontSize: 11, letterSpacing: 0 };
  return { fontSize: 9, letterSpacing: 0 };
}

// Get tier class
function getTierInfo(dollarSize) {
  if (dollarSize >= 100_000_000) return { tier: 'tier-100m', label: '$100M+' };
  if (dollarSize >= 50_000_000) return { tier: 'tier-50m', label: '$50M+' };
  if (dollarSize >= 25_000_000) return { tier: 'tier-25m', label: '$25M+' };
  return { tier: '', label: '' };
}

const ProStatRotator = memo(({ liqVolume, tradeVolume }) => {
  const [showLiq, setShowLiq] = useState(true);
  useEffect(() => {
    const id = setInterval(() => setShowLiq(v => !v), 4000);
    return () => clearInterval(id);
  }, []);
  return (
    <div className="pro-stat-card pro-stat-card--mobile-rotator">
      <div className="pro-stat-card-body">
        <div className="pro-stat-card-label">{showLiq ? '24H LIQ' : '24H VOL'}</div>
        <div className={`pro-stat-card-val${showLiq ? '' : ' pro-stat-card-val--bright'}`}>
          {formatDollar(showLiq ? liqVolume : tradeVolume)}
        </div>
      </div>
    </div>
  );
});

// --- Memoized row components to avoid re-rendering 800+ rows on each state change ---

const TradeRowPro = memo(({ trade, markPrice, formatPrice, onClick }) => {
  const baseCoin = trade.baseCoin || trade.symbol?.replace(/USDT$|USD$/i, '').toUpperCase() || '';
  const isLong = trade.side === 'buy';
  const sideClass = isLong ? 'long' : 'short';
  const { tier, label: tierLabel } = getTierInfo(trade.dollarSize);
  const tradeAge = Date.now() - new Date(trade.timestamp).getTime();
  const isLive = !trade.isHistorical && tradeAge < 2000;
  const flashClass = isLive ? (isLong ? 'flash-long' : 'flash-short') : '';
  const liveClass = isLive ? 'trade-row--live' : '';

  if (tier === 'tier-100m') {
    return (
      <div
        key={`${trade.exchange}-${trade.symbol}-${trade.timestamp}-${trade.dollarSize}`}
        className={`trade-row tier-100m ${isLong ? 'long-row' : 'short-row'} ${liveClass}`}
        onClick={() => onClick(trade)}
      >
        <div className="t100-inner">
          <span className="ex-icon">{getExShort(trade.exchange)}</span>
          <span className="coin-sym" style={coinSymStyle(baseCoin)}>{baseCoin}</span>
          <div className="price-cell">
            <div className="price-cell-row"><span className="price-cell-label">ENT</span><span className="price-cell-val">{formatPrice(trade.price)}</span></div>
            <div className="price-cell-row"><span className="price-cell-label">MRK</span><span className="price-cell-val">{formatPrice(markPrice || trade.price)}</span></div>
          </div>
          <div><span className="trade-time">{formatTime(trade.timestamp)}</span></div>
          <div className="t100-amount-wrap">
            <span className={`t100-amount ${sideClass}`}>{formatDollar(trade.dollarSize)}</span>
            <span className="t100-usd-label">NOTIONAL USD</span>
          </div>
          <span className={`side-tag ${sideClass}`}><span className="side-full">{isLong ? 'LONG' : 'SHORT'}</span><span className="side-abbr">{isLong ? 'L' : 'S'}</span></span>
        </div>
        <div className="t100-wave"><div className={`t100-wave-fill ${sideClass}`} /></div>
      </div>
    );
  }

  return (
    <div
      key={`${trade.exchange}-${trade.symbol}-${trade.timestamp}-${trade.dollarSize}`}
      className={`trade-row ${isLong ? 'long-row' : 'short-row'} ${tier} ${flashClass} ${liveClass}`}
      onClick={() => onClick(trade)}
    >
      {tier === 'tier-50m' && <div className="shimmer-sweep" />}
      <span className="ex-icon">{getExShort(trade.exchange)}</span>
      <span className="coin-sym" style={coinSymStyle(baseCoin)}>{baseCoin}</span>
      <div className="price-cell">
        <div className="price-cell-row"><span className="price-cell-label">ENT</span><span className="price-cell-val">{formatPrice(trade.price)}</span></div>
        <div className="price-cell-row"><span className="price-cell-label">MRK</span><span className="price-cell-val">{formatPrice(markPrice || trade.price)}</span></div>
      </div>
      <div><span className="trade-time">{formatTime(trade.timestamp)}</span></div>
      <div className={`amount-cell ${sideClass}`}>
        {formatDollar(trade.dollarSize)}
      </div>
      <span className={`side-tag ${sideClass}`}><span className="side-full">{isLong ? 'LONG' : 'SHORT'}</span><span className="side-abbr">{isLong ? 'L' : 'S'}</span></span>
    </div>
  );
});
TradeRowPro.displayName = 'TradeRowPro';

const LiqRowPro = memo(({ liq, formatPrice, onClick }) => {
  const baseCoin = liq.baseCoin || liq.symbol?.replace(/USDT$|USD$/i, '').toUpperCase() || '';
  const isLong = liq.side === 'sell';
  const sideClass = isLong ? 'long' : 'short';
  const tradeAge = Date.now() - new Date(liq.timestamp).getTime();
  const isLive = !liq.isHistorical && tradeAge < 2000;
  const liveClass = isLive ? 'liq-row--live' : '';

  return (
    <div
      key={`liq-${liq.exchange}-${liq.symbol}-${liq.timestamp}-${liq.dollarSize}`}
      className={`liq-row ${isLong ? 'long-liq' : 'short-liq'} ${liveClass}`}
      onClick={() => onClick(liq)}
    >
      <span className="ex-icon">{getExShort(liq.exchange)}</span>
      <span className="coin-sym" style={coinSymStyle(baseCoin)}>{baseCoin}</span>
      <div className="liq-price-time">
        <span className="liq-price-val">{formatPrice(liq.price)}</span>
        <span className="liq-time-val">{formatTime(liq.timestamp)}</span>
      </div>
      <div className="liq-amount-wrap">
        <span className={`liq-amount ${sideClass}`}>{formatDollar(liq.dollarSize)}</span>
        <span className={`liq-side-badge ${sideClass}`}>{isLong ? 'L' : 'S'}</span>
      </div>
    </div>
  );
});
LiqRowPro.displayName = 'LiqRowPro';

const PredRowPro = memo(({ trade }) => {
  const isYes = trade.outcome?.toUpperCase() === 'YES';
  const isNo = trade.outcome?.toUpperCase() === 'NO';

  return (
    <div
      key={`pred-${trade.marketTitle}-${trade.timestamp}-${trade.dollarSize}`}
      className={`pred-row ${isYes ? 'pred-yes' : isNo ? 'pred-no' : ''}`}
    >
      <div className="pred-market-wrap">
        {trade.marketIcon && (
          <div className="pred-icon">
            <img src={trade.marketIcon} alt="" />
          </div>
        )}
        <div className="pred-title">{trade.eventTitle || trade.marketTitle}</div>
        <div className="pred-sub">{trade.marketOutcome || trade.outcome} at {((trade.price || 0) * 100).toFixed(0)}{'¢'}</div>
        <div className="pred-time">{formatTimeAgo(trade.timestamp)}</div>
      </div>
      <div className="pred-size">{formatDollar(trade.dollarSize)}</div>
      <span className={`pred-tag ${isYes ? 'yes' : isNo ? 'no' : 'team'}`}>
        {trade.outcome || '-'}
      </span>
    </div>
  );
});
PredRowPro.displayName = 'PredRowPro';

function TradingPagePro() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { localizedPath } = useLanguage();
  const { setPreference } = useInterfacePreference();

  useEffect(() => {
    trackPageView('trades-pro', { interface: 'pro' });
  }, []);

  const showWeex = useFeatureFlag('weex-promo', true);

  // Test mode
  const [isTestMode, setIsTestMode] = useState(false);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    setIsTestMode(params.get('isTestMode') === 'true');
  }, []);

  // State
  const [chartVisible, setChartVisible] = useState(false);
  const [chartSymbol, setChartSymbol] = useState('BTCUSDT');
  const [filterOpen, setFilterOpen] = useState(false);
  const [selectedTrade, setSelectedTrade] = useState(null);
  const [mobileTab, setMobileTab] = useState('whales');
  const [predFilter, setPredFilter] = useState('ALL');
  const [top3Open, setTop3Open] = useState(false);

  // Hooks
  const { filters, setWhaleTradesMinVolume, setLiquidationsMinVolume, setSelectedCoins, toggleCategory, clearCategories, shouldDisplayTrade, formatDollarAmount } = useFilters();

  // Coin filter state
  const [coinInput, setCoinInput] = useState('');
  const [availableCoins, setAvailableCoins] = useState([]);
  const [coinSuggestions, setCoinSuggestions] = useState([]);

  // Load available coins on mount
  useEffect(() => {
    const loadCoins = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/mapped_symbols.json`);
        if (response.ok) {
          const data = await response.json();
          const coinList = Object.keys(data).map(s => s.replace(/USDT$|USD$/, ''));
          setAvailableCoins([...new Set(coinList)].sort());
        }
      } catch (err) {
        console.error('Error loading coins:', err);
      }
    };
    loadCoins();
  }, []);

  const handleCoinInputChange = (value) => {
    const upper = value.toUpperCase();
    setCoinInput(upper);
    if (upper.length > 0) {
      setCoinSuggestions(
        availableCoins.filter(c => !filters.selectedCoins.includes(c) && c.includes(upper)).slice(0, 8)
      );
    } else {
      setCoinSuggestions([]);
    }
  };

  const handleAddCoin = (coin) => {
    if (!filters.selectedCoins.includes(coin)) {
      setSelectedCoins([...filters.selectedCoins, coin]);
    }
    setCoinInput('');
    setCoinSuggestions([]);
  };

  const handleRemoveCoin = (coin) => {
    setSelectedCoins(filters.selectedCoins.filter(c => c !== coin));
  };
  const {
    whaleTradesEnabled, toggleWhaleTradesSound,
    liquidationsEnabled, toggleLiquidationsSound
  } = useSoundNotificationsContext();
  const { topTrades: top3Trades } = useTopTrades('whale_trade', 3);

  const {
    isConnected,
    isSubscribed,
    whaleTrades: liveWhaleTrades,
    liquidations: liveLiquidations,
    historicalTrades,
    historicalLoading: loading,
    summary,
    latency,
    simulateTrade,
    simulateLiquidation,
    subscribe: manualSubscribe,
    unsubscribe: manualUnsubscribe
  } = useWebSocket(filters, { enabled: true });

  const { prices, formatPrice } = useLivePrices();
  const { data: btcPredictions, timeframe: btcTimeframe } = useBtcPredictions();
  const { data: tradeSizeDistribution, formatRange, totalTrades: distTotalTrades } = useTradeSizeDistribution();
  const { news: coinNews } = useAllCoinNews();

  const polyFilters = useMemo(() => ({ minVolume: 10000 }), []);
  const {
    trades: polyTrades,
    summary: polySummary,
    historicalLoading: polyLoading
  } = usePolymarketWebSocket(polyFilters);

  const { today: etfToday, formatFlow } = useEtfFlows();
  const signalData = useSignal();

  const {
    positions: hlPositions,
    isConnected: hlConnected
  } = useHyperliquidPositions({ minPositionSize: 25000, side: 'all' });

  // Set document title with live BTC price
  useEffect(() => {
    const btcPrice = prices['BTC'];
    if (btcPrice) {
      document.title = `BTC $${Number(btcPrice).toLocaleString('en-US', { maximumFractionDigits: 0 })} - Tradermap Crypto Terminal`;
    }
  }, [prices]);

  // Restore title only on unmount
  useEffect(() => {
    return () => { document.title = 'TraderMap'; };
  }, []);

  // Split historical trades
  const historicalWhaleTrades = useMemo(() => historicalTrades.filter(t => !t.isLiquidation), [historicalTrades]);
  const historicalLiquidations = useMemo(() => historicalTrades.filter(t => t.isLiquidation), [historicalTrades]);

  // Combine & deduplicate whale trades
  const allWhaleTrades = useMemo(() => {
    const map = new Map();
    historicalWhaleTrades.forEach(trade => {
      map.set(`${trade.exchange}-${trade.symbol}-${trade.timestamp}-${trade.price}`, trade);
    });
    liveWhaleTrades.forEach(trade => {
      map.set(`${trade.exchange}-${trade.symbol}-${trade.timestamp}-${trade.price}`, trade);
    });
    let combined = Array.from(map.values());
    combined = combined.filter(trade => shouldDisplayTrade(trade));
    combined.sort((a, b) => {
      const aT = typeof a.timestamp === 'number' ? a.timestamp : new Date(a.timestamp).getTime();
      const bT = typeof b.timestamp === 'number' ? b.timestamp : new Date(b.timestamp).getTime();
      return bT - aT;
    });
    return combined.slice(0, 300);
  }, [historicalWhaleTrades, liveWhaleTrades, shouldDisplayTrade]);

  // Combine & deduplicate liquidations
  const allLiquidations = useMemo(() => {
    const map = new Map();
    historicalLiquidations.forEach(liq => {
      map.set(`${liq.exchange}-${liq.symbol}-${liq.timestamp}-${liq.price}`, liq);
    });
    liveLiquidations.forEach(liq => {
      map.set(`${liq.exchange}-${liq.symbol}-${liq.timestamp}-${liq.price}`, liq);
    });
    let combined = Array.from(map.values());
    combined = combined.filter(trade => shouldDisplayTrade({ ...trade, isLiquidation: true }));
    combined.sort((a, b) => {
      const aT = typeof a.timestamp === 'number' ? a.timestamp : new Date(a.timestamp).getTime();
      const bT = typeof b.timestamp === 'number' ? b.timestamp : new Date(b.timestamp).getTime();
      return bT - aT;
    });
    return combined.slice(0, 300);
  }, [historicalLiquidations, liveLiquidations, shouldDisplayTrade]);

  // Dynamic prediction categories
  const predCategories = useMemo(() => {
    const cats = new Set();
    polyTrades.forEach(t => {
      t.categories?.forEach(c => cats.add(c.toUpperCase()));
    });
    return ['ALL', ...Array.from(cats).sort()];
  }, [polyTrades]);

  // Filtered polymarket trades
  const filteredPolyTrades = useMemo(() => {
    if (predFilter === 'ALL') return polyTrades;
    return polyTrades.filter(t => t.categories?.some(c => c.toUpperCase().includes(predFilter)));
  }, [polyTrades, predFilter]);

  // Whale trade counts
  const whaleLongCount = useMemo(() => allWhaleTrades.filter(t => t.side === 'buy').length, [allWhaleTrades]);
  const whaleShortCount = useMemo(() => allWhaleTrades.filter(t => t.side !== 'buy').length, [allWhaleTrades]);

  // Liq counts (sell = LONG liquidated, buy = SHORT liquidated)
  const liqLongCount = useMemo(() => allLiquidations.filter(t => t.side === 'sell').length, [allLiquidations]);
  const liqShortCount = useMemo(() => allLiquidations.filter(t => t.side !== 'sell').length, [allLiquidations]);

  // Pred counts
  const predYesCount = useMemo(() => filteredPolyTrades.filter(t => t.outcome?.toUpperCase() === 'YES').length, [filteredPolyTrades]);
  const predNoCount = useMemo(() => filteredPolyTrades.filter(t => t.outcome?.toUpperCase() !== 'YES').length, [filteredPolyTrades]);

  // Ticker prices
  const tickerCoins = useMemo(() => {
    const coins = ['BTC', 'ETH', 'SOL', 'BNB', 'XRP', 'DOGE', 'ADA', 'AVAX', 'DOT', 'LINK', 'MATIC', 'UNI', 'APT', 'ARB', 'OP', 'SUI', 'TIA', 'SEI', 'INJ', 'FET'];
    return coins.filter(c => prices[c]).map(c => ({ symbol: c, price: prices[c] }));
  }, [prices]);

  // Trade size distribution
  const sizeDistribution = useMemo(() => {
    if (!tradeSizeDistribution) return [];
    const ranges = ['10K-100K', '100K-500K', '500K-1M', '1M-10M', '10M+'];
    return ranges.map(r => ({
      label: formatRange(r),
      count: tradeSizeDistribution[r]?.count || 0,
      long: tradeSizeDistribution[r]?.long || 0,
      short: tradeSizeDistribution[r]?.short || 0
    }));
  }, [tradeSizeDistribution, formatRange]);

  // Total for size distribution
  const sizeTotals = useMemo(() => {
    // Weight by trade count — a range with more trades has more influence on totals
    let totalLong = 0;
    let totalShort = 0;
    let totalCount = 0;
    sizeDistribution.forEach(r => {
      totalLong += Math.round((r.long / 100) * r.count);
      totalShort += Math.round((r.short / 100) * r.count);
      totalCount += r.count;
    });
    return { long: totalLong, short: totalShort, count: totalCount };
  }, [sizeDistribution]);

  // Chart open
  const handleOpenChart = (symbol) => {
    setChartSymbol(symbol);
    setChartVisible(true);
    setSelectedTrade(null);
  };

  // Stable click handler for memo'd row components
  const handleRowClick = useCallback((trade) => {
    setSelectedTrade(trade);
  }, []);

  // Whale total volume
  const whaleVolume = useMemo(() => allWhaleTrades.reduce((s, t) => s + (t.dollarSize || 0), 0), [allWhaleTrades]);
  const liqVolume = useMemo(() => allLiquidations.reduce((s, t) => s + (t.dollarSize || 0), 0), [allLiquidations]);

  return (
    <div className="trading-page-pro">
      <SEO
        page="terminal"
        skipTitle
        title="Tradermap Crypto Terminal | Free Whale Tracking"
        description="Prediction trades, crypto whale trades, liquidations and real-time news feed with Tradermap Crypto Terminal. Free crypto tracking tool."
        image="https://tradermap.io/og-terminal.png"
      />

      <ProHeader
        isConnected={isConnected}
        isSubscribed={isSubscribed}
        onToggleChart={() => setChartVisible(v => !v)}
        chartVisible={chartVisible}
        onOpenFilter={() => setFilterOpen(true)}
      />

      {/* Debug Panel - shown when ?isTestMode=true */}
      {isTestMode && (
        <DebugPanel
          onSimulateTrade={simulateTrade}
          onSimulateLiquidation={simulateLiquidation}
          onSubscribe={manualSubscribe}
          onUnsubscribe={manualUnsubscribe}
          isConnected={isConnected}
          isSubscribed={isSubscribed}
        />
      )}

      {/* TradingView Chart */}
      <ProChart visible={chartVisible} symbol={chartSymbol} />

      {/* Ticker Bar */}
      <div className="pro-ticker-wrap">
        <div className="pro-ticker-label">MKT</div>
        <div className="pro-ticker-scroll">
          {[...tickerCoins, ...tickerCoins].map((coin, i) => (
            <div key={`${coin.symbol}-${i}`} className="pro-ticker-item">
              <span className="pro-ticker-sym">{coin.symbol}</span>
              <span className="pro-ticker-price">${formatPrice(coin.price)}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Metrics Bar */}
      <div className="pro-metrics-bar" data-weex={showWeex || undefined}>
        {/* ETF Flow Cards */}
        {['BTC', 'ETH', 'SOL'].map(asset => {
          const flow = etfToday?.[asset];
          if (!flow?.netFlow && flow?.netFlow !== 0) return null;
          const isNeg = flow.netFlow < 0;
          return (
            <div key={asset} className="etf-card">
              <span className="etf-label">{asset} ETF<br />NET FLOW</span>
              <div className="etf-divider" />
              <div>
                <div className={`etf-value ${isNeg ? 'neg' : 'pos'}`}>{formatFlow(flow.netFlow)}</div>
                {flow.date && <div className="etf-date">{new Date(flow.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</div>}
              </div>
            </div>
          );
        })}

        <SignalWidget signal={signalData} />

        <HyperliquidPromo shape="square" />
        {showWeex && <BitunixPromo shape="square" />}

        {/* 24h Liquidation \u2014 desktop only */}
        <div className="pro-stat-card pro-stat-card--desktop">
          <span className="pro-stat-card-icon">{'\u23F3'}</span>
          <div className="pro-stat-card-body">
            <div className="pro-stat-card-label">24H LIQUIDATION</div>
            <div className="pro-stat-card-val">{formatDollar(summary?.liquidations?.totalVolume)}</div>
          </div>
        </div>

        {/* 24h Volume \u2014 desktop only */}
        <div className="pro-stat-card pro-stat-card--desktop">
          <span className="pro-stat-card-icon">{'\u23F3'}</span>
          <div className="pro-stat-card-body">
            <div className="pro-stat-card-label">24H VOLUME</div>
            <div className="pro-stat-card-val pro-stat-card-val--bright">{formatDollar(summary?.whaleTrades?.totalVolume)}</div>
          </div>
        </div>

        {/* 24h combined \u2014 mobile only, rotates between liq and volume */}
        <ProStatRotator liqVolume={summary?.liquidations?.totalVolume} tradeVolume={summary?.whaleTrades?.totalVolume} />

        {showWeex && (
          <button className="pro-metrics-filter-btn" onClick={() => setFilterOpen(true)}>
            &#9636; FILTER
          </button>
        )}
      </div>

      {/* Mobile Tabs */}
      <div className="pro-mobile-tabs">
        <button className={`pro-mobile-tab ${mobileTab === 'whales' ? 'pro-mobile-tab--active' : ''}`} onClick={() => setMobileTab('whales')}>Whales</button>
        <button className={`pro-mobile-tab ${mobileTab === 'liqs' ? 'pro-mobile-tab--active' : ''}`} onClick={() => setMobileTab('liqs')}>Liqs</button>
        <button className={`pro-mobile-tab ${mobileTab === 'preds' ? 'pro-mobile-tab--active' : ''}`} onClick={() => setMobileTab('preds')}>Preds</button>
        <button className={`pro-mobile-tab ${mobileTab === 'news' ? 'pro-mobile-tab--active' : ''}`} onClick={() => setMobileTab('news')}>News</button>
      </div>

      {/* Main Grid — 4 columns */}
      <div className="pro-main-grid">

        {/* Panel 1: Whale Trades */}
        <div className={`pro-panel ${mobileTab !== 'whales' ? 'pro-panel--hidden' : ''}`}>
          <div className="pro-panel-header">
            <span className="pro-panel-title"><span className="panel-icon">{'\u25C8'} </span><span className="title-full">WHALE TRADES</span><span className="title-short">WT</span></span>
            <button className={`pro-sound-btn ${whaleTradesEnabled ? 'on' : ''}`} onClick={toggleWhaleTradesSound} title="Toggle whale trades sound">
              {whaleTradesEnabled ? '\u266A' : '\u2716'}
            </button>
            <div className="pro-panel-stat">
              <span className="pro-stat-long">{'\u25B2'} {whaleLongCount} L</span>
              <span className="pro-stat-short">{'\u25BC'} {whaleShortCount} S</span>
            </div>
            <span className="pro-panel-vol">{formatDollar(whaleVolume)}</span>
            <div className="pro-top3-wrap">
              <button className={`pro-sound-btn ${top3Open ? 'on' : ''}`} onClick={() => setTop3Open(v => !v)} title="Top 3 largest trades">
                {'\u2605'}
              </button>
              {top3Open && top3Trades.length > 0 && (
                <div className="pro-top3-dropdown">
                  <div className="pro-top3-title">TOP 3 ALL TIME</div>
                  {top3Trades.map((t, i) => {
                    const coin = t.symbol?.replace(/USDT$|USD$/i, '').toUpperCase() || '';
                    const isLong = t.side === 'buy';
                    return (
                      <div key={i} className="pro-top3-item" onClick={() => { handleOpenChart(t.symbol); setTop3Open(false); }}>
                        <span className="pro-top3-rank">#{i + 1}</span>
                        {t.logoUrl && <img className="pro-top3-icon" src={t.logoUrl} alt={coin} />}
                        <span className="pro-top3-coin">{coin}</span>
                        <span className="pro-top3-vol">{formatDollar(t.dollarSize)}</span>
                        <span className={`pro-top3-side ${isLong ? 'long' : 'short'}`}>{isLong ? 'LONG' : 'SHORT'}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <button className="pro-filter-trigger" onClick={() => setFilterOpen(true)}>
              {'\u229F'} FILTER
            </button>
          </div>
          <div className="pro-col-header">
            <span>EX</span>
            <span>PAIR</span>
            <span style={{ paddingLeft: 8 }}>PRICES</span>
            <span>TIME</span>
            <span style={{ textAlign: 'right' }}>SIZE</span>
            <span>SIDE</span>
          </div>
          <div className="pro-trades-list">
            {loading ? (
              <div className="pro-loading">Loading trades...</div>
            ) : allWhaleTrades.length === 0 ? (
              <div className="pro-empty">No trades</div>
            ) : (
              allWhaleTrades.map(trade => {
                const baseCoin = trade.baseCoin || trade.symbol?.replace(/USDT$|USD$/i, '').toUpperCase() || '';
                return (
                  <TradeRowPro
                    key={`${trade.exchange}-${trade.symbol}-${trade.timestamp}-${trade.dollarSize}`}
                    trade={trade}
                    markPrice={prices[baseCoin]}
                    formatPrice={formatPrice}
                    onClick={handleRowClick}
                  />
                );
              })
            )}
          </div>
        </div>

        {/* Panel 2: Liquidations */}
        <div className={`pro-panel ${mobileTab !== 'liqs' ? 'pro-panel--hidden' : ''}`}>
          <div className="pro-panel-header">
            <span className="pro-panel-title"><span className="panel-icon">{'\u26A1'} </span><span className="title-full">LIQUIDATIONS</span><span className="title-short">LIQ</span></span>
            <button className={`pro-sound-btn ${liquidationsEnabled ? 'on' : ''}`} onClick={toggleLiquidationsSound} title="Toggle liquidations sound">
              {liquidationsEnabled ? '\u266A' : '\u2716'}
            </button>
            <div className="pro-panel-stat">
              <span className="pro-stat-long">{'\u25B2'} {liqLongCount} L</span>
              <span className="pro-stat-short">{'\u25BC'} {liqShortCount} S</span>
            </div>
            <span className="pro-panel-vol">{formatDollar(liqVolume)}</span>
          </div>
          <div className="pro-liq-col-header">
            <span>EX</span>
            <span>PAIR</span>
            <span>LIQ PRICE</span>
            <span>AMOUNT</span>
          </div>
          <div className="pro-trades-list">
            {loading ? (
              <div className="pro-loading">Loading...</div>
            ) : allLiquidations.length === 0 ? (
              <div className="pro-empty">No liquidations</div>
            ) : (
              allLiquidations.map(liq => (
                <LiqRowPro
                  key={`liq-${liq.exchange}-${liq.symbol}-${liq.timestamp}-${liq.dollarSize}`}
                  liq={liq}
                  formatPrice={formatPrice}
                  onClick={handleRowClick}
                />
              ))
            )}
          </div>
        </div>

        {/* Panel 3: Prediction Trades */}
        <div className={`pro-panel ${mobileTab !== 'preds' ? 'pro-panel--hidden' : ''}`}>
          <div className="pro-panel-header">
            <span className="pro-panel-title"><span className="panel-icon">{'\u25CE'} </span><span className="title-full">PREDICTION</span><span className="title-short">PRED</span></span>
            <div className="pro-panel-stat">
              <span className="pro-stat-long">{'\u25B2'} {predYesCount} Y</span>
              <span className="pro-stat-short">{'\u25BC'} {predNoCount} N</span>
            </div>
          </div>
          <div className="pro-pred-filter">
            {predCategories.map(cat => (
              <button
                key={cat}
                className={`pred-filter-btn ${predFilter === cat ? 'active' : ''}`}
                onClick={() => setPredFilter(cat)}
              >
                {cat}
              </button>
            ))}
          </div>
          <div className="pro-pred-col-header">
            <span>MARKET</span>
            <span style={{ textAlign: 'right' }}>SIZE</span>
            <span style={{ textAlign: 'right' }}>SIDE</span>
          </div>
          <div className="pro-trades-list">
            {polyLoading ? (
              <div className="pro-loading">Loading predictions...</div>
            ) : filteredPolyTrades.length === 0 ? (
              <div className="pro-empty">No prediction trades</div>
            ) : (
              filteredPolyTrades.slice(0, 200).map(trade => (
                <PredRowPro
                  key={`pred-${trade.marketTitle}-${trade.timestamp}-${trade.dollarSize}`}
                  trade={trade}
                />
              ))
            )}
          </div>
        </div>

        {/* Panel 4: Right Sidebar */}
        <div className={`pro-panel pro-right-col ${mobileTab !== 'news' && mobileTab !== 'stats' ? 'pro-panel--hidden' : ''}`}>

          {/* News */}
          <div className="pro-news-section">
            <div className="pro-section-header">
              <span className="pro-section-title">{'\u25A4'} CRYPTO FEED</span>
              <span className="pro-section-note">AUTO-REFRESH 30S</span>
            </div>
            <div className="pro-news-scroll">
              {coinNews && coinNews.length > 0 ? (
                coinNews.slice(0, 30).map((item, i) => (
                  <div
                    key={item.guid || i}
                    className="pro-news-item"
                  >
                    <div className="pro-news-meta">
                      <span className="pro-news-src">{item.source?.toUpperCase()}</span>
                      <div className="pro-news-dot" />
                      <span className="pro-news-time">{formatTimeAgo(item.pubDate).toUpperCase()}</span>
                    </div>
                    <div className="pro-news-title">{item.title}</div>
                    {item.coins && item.coins.length > 0 && (
                      <div className="pro-news-coins">
                        {item.coins.map(coin => (
                          <span key={coin} className="pro-news-coin-tag">{coin}</span>
                        ))}
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <div className="pro-empty">No news</div>
              )}
            </div>
          </div>

          {/* Size Distribution */}
          <div className="pro-bvs-section">
            <div className="pro-section-header">
              <span className="pro-section-title">{'\u25A7'} SIZE DISTRIBUTION (24H)</span>
            </div>
            <div className="pro-bvs-grid">
              {sizeDistribution.length > 0 ? (
                <>
                  {sizeDistribution.map((row, i) => {
                    const total = row.long + row.short || 1;
                    const longPct = Math.round((row.long / total) * 100);
                    const shortPct = 100 - longPct;
                    return (
                      <div key={i} className="bvs-row">
                        <span className="bvs-label">{row.label}</span>
                        <div className="bvs-bar">
                          <div className="bvs-long" style={{ width: `${longPct}%` }} />
                          <div className="bvs-short" />
                          <div className="bvs-pcts">
                            <span className="bvs-long-pct">{longPct}%</span>
                            <span className="bvs-short-pct">{shortPct}%</span>
                          </div>
                        </div>
                        <span className="bvs-count">{row.count.toLocaleString()}</span>
                      </div>
                    );
                  })}
                  {/* Total row */}
                  {sizeTotals.count > 0 && (() => {
                    const total = sizeTotals.long + sizeTotals.short || 1;
                    const longPct = Math.round((sizeTotals.long / total) * 100);
                    return (
                      <div className="bvs-total-row">
                        <span className="bvs-total-label">TOTAL</span>
                        <div className="bvs-bar">
                          <div className="bvs-long" style={{ width: `${longPct}%` }} />
                          <div className="bvs-short" />
                          <div className="bvs-pcts">
                            <span className="bvs-long-pct">{longPct}%</span>
                            <span className="bvs-short-pct">{100 - longPct}%</span>
                          </div>
                        </div>
                        <span className="bvs-count" style={{ color: 'var(--text)' }}>{sizeTotals.count.toLocaleString()}</span>
                      </div>
                    );
                  })()}
                </>
              ) : (
                <div className="pro-empty">No data</div>
              )}
            </div>
          </div>

          {/* Hyperliquid Positions */}
          <div className="pro-hl-section">
            <div className="pro-section-header">
              <span className="pro-section-title">{'\u25EC'} HYPERLIQUID</span>
              {hlConnected ? (
                <span className="pro-section-link" onClick={() => navigate(localizedPath('/hyperliquid'))}>ALL {'\u00BB'}</span>
              ) : (
                <span className="pro-section-note">CONNECTING...</span>
              )}
            </div>
            <div className="pro-hl-col-header">
              <span>USER</span>
              <span>SYM</span>
              <span>VOL</span>
              <span>LVG</span>
              <span>PNL</span>
            </div>
            <div className="pro-hl-list">
              {hlPositions && hlPositions.length > 0 ? (
                [...hlPositions].sort((a, b) => (b.positionValue || 0) - (a.positionValue || 0)).slice(0, 30).map((pos, i) => {
                  const isLong = pos.side === 'long';
                  const pnlPos = (pos.unrealizedPnl || 0) >= 0;
                  const roe = pos.returnOnEquity != null ? (pos.returnOnEquity * 100).toFixed(1) : null;
                  return (
                    <div key={`hl-${pos.user}-${pos.symbol}-${i}`} className={`hl-row ${isLong ? 'hl-long' : 'hl-short'}`}>
                      <span className="hl-user">{pos.user?.slice(0, 6)}...{pos.user?.slice(-4)}</span>
                      <span className="hl-sym">{pos.symbol}</span>
                      <div className="hl-vol-wrap">
                        <div className="hl-vol">{formatDollar(pos.positionValue)}</div>
                      </div>
                      <span className="hl-lev">{pos.leverage ? `${pos.leverage}x` : '-'}</span>
                      <div className="hl-pnl-wrap">
                        <div className={`hl-pnl ${pnlPos ? 'pos' : 'neg'}`}>{formatDollar(Math.abs(pos.unrealizedPnl || 0))}</div>
                        {roe && <div className={`hl-pnl-pct ${pnlPos ? 'pos' : 'neg'}`}>{pnlPos ? '+' : '-'}{Math.abs(roe)}%</div>}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="pro-empty">No positions</div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Status Bar */}
      <div className="pro-statusbar">
        <div className="pro-status-item">SYS <span>{isConnected ? 'NOMINAL' : 'OFFLINE'}</span></div>
        <div className="pro-status-sep" />
        <div className="pro-status-item">LATENCY <span>{latency != null ? `${latency}ms` : '--'}</span></div>
        <div className="pro-status-sep" />
        <div className="pro-status-item">FEED <span>BINANCE {'\u00B7'} BYBIT {'\u00B7'} OKX {'\u00B7'} HYPERLIQUID</span></div>
        <div className="pro-status-sep" />
        <div className="pro-status-item">WHALE <span>{formatDollarAmount(filters.whaleTradesMinVolume)}</span></div>
        <div className="pro-status-sep" />
        <div className="pro-status-item" style={{ marginLeft: 'auto' }}>
          TRADERMAP PRO v2.4<span className="cursor-blink" />
        </div>
      </div>

      {/* Filter Modal (top-left) */}
      {filterOpen && (
        <>
          <div className="pro-filter-overlay" onClick={() => setFilterOpen(false)} />
          <div className="pro-filter-modal">
            <div className="pro-filter-modal-header">
              <span className="pro-filter-modal-title">{'\u25A4'} FILTERS</span>
              <button className="pro-filter-modal-close" onClick={() => setFilterOpen(false)}>{'\u2715'}</button>
            </div>
            <div className="pro-filter-section">
              <div className="pro-filter-row">
                <span className="pro-filter-label">Whale Trades Min</span>
                <span className="pro-filter-val">{formatDollarAmount(filters.whaleTradesMinVolume)}</span>
              </div>
              <input
                type="range"
                className="pro-filter-slider"
                min={25000}
                max={10000000}
                step={25000}
                value={filters.whaleTradesMinVolume}
                onChange={(e) => setWhaleTradesMinVolume(Number(e.target.value))}
              />
            </div>
            <div className="pro-filter-section">
              <div className="pro-filter-row">
                <span className="pro-filter-label">Liquidations Min</span>
                <span className="pro-filter-val">{formatDollarAmount(filters.liquidationsMinVolume)}</span>
              </div>
              <input
                type="range"
                className="pro-filter-slider"
                min={10000}
                max={5000000}
                step={10000}
                value={filters.liquidationsMinVolume}
                onChange={(e) => setLiquidationsMinVolume(Number(e.target.value))}
              />
            </div>
            <div className="pro-filter-section">
              <div className="pro-filter-row">
                <span className="pro-filter-label">Coins</span>
                {filters.selectedCoins.length > 0 && (
                  <span className="pro-filter-val">{filters.selectedCoins.length} selected</span>
                )}
              </div>
              <div className="pro-filter-coin-input-wrap">
                <input
                  type="text"
                  className="pro-filter-coin-input"
                  placeholder="Type coin..."
                  value={coinInput}
                  onChange={(e) => handleCoinInputChange(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && coinInput.trim()) {
                      const coin = coinInput.trim().toUpperCase();
                      if (availableCoins.includes(coin)) handleAddCoin(coin);
                    }
                  }}
                  autoComplete="off"
                />
                {coinSuggestions.length > 0 && (
                  <div className="pro-filter-coin-suggestions">
                    {coinSuggestions.map(coin => (
                      <div key={coin} className="pro-filter-coin-suggestion" onClick={() => handleAddCoin(coin)}>{coin}</div>
                    ))}
                  </div>
                )}
              </div>
              {filters.selectedCoins.length > 0 && (
                <div className="pro-filter-coin-chips">
                  {filters.selectedCoins.map(coin => (
                    <span key={coin} className="pro-filter-coin-chip">
                      {coin}
                      <button className="pro-filter-coin-chip-x" onClick={() => handleRemoveCoin(coin)}>{'\u2715'}</button>
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div className="pro-filter-section">
              <div className="pro-filter-row">
                <span className="pro-filter-label">Asset Type</span>
                {filters.selectedCategories && filters.selectedCategories.length > 0 && (
                  <span className="pro-filter-val">{filters.selectedCategories.length} selected</span>
                )}
              </div>
              <div className="pro-filter-category-pills">
                <button
                  className={`pro-filter-category-pill ${(!filters.selectedCategories || filters.selectedCategories.length === 0) ? 'active' : ''}`}
                  onClick={() => clearCategories()}
                >
                  All
                </button>
                {[
                  { value: 'crypto', label: 'Crypto' },
                  { value: 'stock', label: 'Stocks' },
                  { value: 'commodity', label: 'Commodities' },
                  { value: 'index', label: 'Indices' },
                  { value: 'forex', label: 'Forex' },
                ].map(cat => (
                  <button
                    key={cat.value}
                    className={`pro-filter-category-pill ${(filters.selectedCategories || []).includes(cat.value) ? 'active' : ''}`}
                    onClick={() => toggleCategory(cat.value)}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="pro-filter-footer">
              <button className="pro-filter-btn reset" onClick={() => { setWhaleTradesMinVolume(200000); setLiquidationsMinVolume(50000); setSelectedCoins([]); clearCategories(); }}>RESET</button>
              <button className="pro-filter-btn apply" onClick={() => setFilterOpen(false)}>APPLY</button>
            </div>
          </div>
        </>
      )}

      {/* Trade Tooltip */}
      {selectedTrade && (
        <div className="pro-tooltip-overlay" onClick={() => setSelectedTrade(null)}>
          <div className="pro-tooltip-card" onClick={(e) => e.stopPropagation()}>
            <div className="pro-tooltip-header">
              <span className="pro-tooltip-pair">
                {selectedTrade.baseCoin || selectedTrade.symbol?.replace(/USDT$|USD$/i, '').toUpperCase()}
                {selectedTrade.isLiquidation && <span style={{ fontSize: 9, color: 'var(--red)', letterSpacing: '1px' }}>LIQ</span>}
              </span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 9, color: 'var(--text-dim)', letterSpacing: '0.5px' }}>{formatTimeAgo(selectedTrade.timestamp)}</span>
                <button className="pro-tooltip-close" onClick={() => setSelectedTrade(null)}>{'\u2715'}</button>
              </div>
            </div>
            <div className="pro-tooltip-body">
              <div className="pro-tooltip-row"><span className="pro-tooltip-label">Exchange:</span><span className="pro-tooltip-val">{selectedTrade.exchange}</span></div>
              <div className="pro-tooltip-row"><span className="pro-tooltip-label">Live Price:</span><span className="pro-tooltip-val">{formatPrice(prices[selectedTrade.baseCoin || selectedTrade.symbol?.replace(/USDT$|USD$/i, '').toUpperCase()] || selectedTrade.price)}</span></div>
              <div className="pro-tooltip-row"><span className="pro-tooltip-label">Entry Price:</span><span className="pro-tooltip-val">{formatPrice(selectedTrade.price)}</span></div>
              <div className="pro-tooltip-row"><span className="pro-tooltip-label">Volume:</span><span className="pro-tooltip-val">{formatDollar(selectedTrade.dollarSize)}</span></div>
              <div className="pro-tooltip-row">
                <span className="pro-tooltip-label">Side:</span>
                {selectedTrade.isLiquidation ? (
                  <span className={`pro-tooltip-val ${selectedTrade.side === 'sell' ? 'liq-long' : 'liq-short'}`}>{selectedTrade.side === 'sell' ? 'LONG LIQ' : 'SHORT LIQ'}</span>
                ) : (
                  <span className={`pro-tooltip-val ${selectedTrade.side === 'buy' ? 'long' : 'short'}`}>{selectedTrade.side === 'buy' ? 'LONG' : 'SHORT'}</span>
                )}
              </div>
            </div>
            <div className="pro-tooltip-actions">
              <div className="pro-tooltip-btn-row">
                <button className="pro-tooltip-btn" onClick={() => handleOpenChart(selectedTrade.symbol)}>Live Chart</button>
                <button className="pro-tooltip-btn" onClick={() => {
                  const coin = selectedTrade.baseCoin || selectedTrade.symbol?.replace(/USDT$|USD$/i, '').toUpperCase();
                  navigate(localizedPath(`/price/${coin.toLowerCase()}`));
                  setSelectedTrade(null);
                }}>Coin Details</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TradingPagePro;
